import Swal from 'sweetalert2'
import { ref } from 'vue'
import { isNativeApp, nativeDeviceModel, nativePlugin, onAppPause, onAppResume } from '@/utils/nativeApp'
import { enableBiometricLogin, revokeMyBiometricLogin, signInWithBiometric } from '@/services/wootdashBiometric'
import type { WootDashSession } from '@/services/auth'

// Entrar com digital no app de celular. O token de entrada fica no cofre do Android (plugin nativo WootDashBiometric,
// ionic/…/WootDashBiometricPlugin.java), numa chave que só a digital ou a senha do aparelho abre — aqui no painel só passa
// de mão em mão, nunca vai pro localStorage. Sair (logout) não desliga: a ideia é justamente voltar com a digital.

/** Já ofereceu a digital neste celular (não pergunta a cada login). Volta a oferecer se a digital deixar de valer. */
const OFFERED_KEY = 'wootdash.app.digital-oferecida'
/** De quem é a digital deste celular (só nome e id, pra tela de login dizer "Entrar como…"). */
const OWNER_KEY = 'wootdash.app.digital-dono'
/** Ativação que morreu no celular (digital nova cadastrada) e ainda está na Api: desliga no próximo login. */
const PENDING_REVOKE_KEY = 'wootdash.app.digital-revogar'
const CANCELLED = 'Ativação cancelada.'
/** sessionStorage: performLogout marca, pra tela de login não pedir a digital sozinha logo depois de "Sair". */
export const SKIP_BIOMETRIC_PROMPT_KEY = 'wootdash.app.sem-digital-automatica'

export interface BiometricStatus { available: boolean; reason: string; hasCredential: boolean; id: string | null }
export interface BiometricOwner { agentId: string; name: string }
export type BiometricSignIn = { session: WootDashSession } | { problem: string } | { cancelled: true; usePassword: boolean }

function plugin() {
  return nativePlugin('WootDashBiometric')
}

function write(action: (s: Storage) => void) {
  try { action(localStorage) } catch { /* modo privado / sem storage */ }
}

function read(key: string): string | null {
  try { return localStorage.getItem(key) } catch { return null }
}

/** Estado da digital neste celular; nulo fora do app (ou APK antigo, sem o plugin). */
export async function biometricStatus(): Promise<BiometricStatus | null> {
  const p = plugin()
  if (!p?.status) return null
  try { return await p.status() } catch { return null }
}

export function biometricOwner(): BiometricOwner | null {
  try { return JSON.parse(read(OWNER_KEY) ?? 'null') } catch { return null }
}

function forgetCredential() {
  write(s => { s.removeItem(OWNER_KEY); s.removeItem(OFFERED_KEY) })
}

/** Ativa neste celular (oferta depois do login ou Meu perfil). Nulo = ativou; texto = o problema. */
export async function enableBiometricHere(authToken: string, owner: BiometricOwner): Promise<string | null> {
  const p = plugin()
  if (!p?.enable) return 'Atualize o app do WootDash pra usar a digital.'
  const created = await enableBiometricLogin(authToken, await nativeDeviceModel())
  if ('problem' in created) return created.problem
  const result = await p.enable({ token: created.token, id: created.id }).catch(() => null)
  if (!result?.ok) {
    await revokeMyBiometricLogin(authToken, created.id) // não ficou no celular: não deixa valendo na Api
    return result?.cancelled ? CANCELLED : result?.error ?? 'Não foi possível ativar a digital.'
  }
  write(s => { s.setItem(OWNER_KEY, JSON.stringify(owner)); s.setItem(OFFERED_KEY, '1') })
  return null
}

/** Desliga neste celular e na Api. */
export async function disableBiometricHere(authToken: string): Promise<void> {
  const result = await plugin()?.disable?.().catch(() => null)
  if (result?.id) await revokeMyBiometricLogin(authToken, result.id)
  write(s => s.removeItem(OWNER_KEY)) // continua marcado como oferecido: quem desligou não quer a pergunta de novo
}

/**
 * Depois de entrar com a senha no app: desliga na Api a ativação que tinha morrido no celular e oferece a digital, uma
 * vez por celular, se o aparelho tiver leitor (ou senha de tela) e o admin deixar.
 */
export async function offerBiometricAfterLogin(authToken: string, owner: BiometricOwner, allowedBySettings: boolean): Promise<void> {
  if (!isNativeApp()) return
  const pending = read(PENDING_REVOKE_KEY)
  if (pending && await revokeMyBiometricLogin(authToken, pending)) write(s => s.removeItem(PENDING_REVOKE_KEY))

  if (!allowedBySettings || read(OFFERED_KEY)) return
  const status = await biometricStatus()
  if (!status?.available || status.hasCredential) return
  write(s => s.setItem(OFFERED_KEY, '1'))

  const answer = await Swal.fire({
    icon: 'question',
    title: 'Entrar com a digital?',
    html: 'Da próxima vez, entre no WootDash com a <b>digital</b> (ou a senha do celular), sem digitar a senha. A sua senha não fica guardada no celular. Dá pra desligar quando quiser em Meu perfil.',
    showCancelButton: true, confirmButtonText: 'Ativar', cancelButtonText: 'Agora não', confirmButtonColor: '#bc0c24',
  })
  if (!answer.isConfirmed) return
  const problem = await enableBiometricHere(authToken, owner)
  if (!problem) void Swal.fire({ icon: 'success', title: 'Digital ativada', text: 'Na próxima vez é só usar a digital pra entrar.', timer: 2500, showConfirmButton: false })
  else if (problem !== CANCELLED) void Swal.fire({ icon: 'warning', title: 'Digital não ativada', text: problem })
}

/** Tela de login: pede a digital e troca o token por uma sessão. */
export async function signInWithBiometricHere(): Promise<BiometricSignIn> {
  const result = await plugin()?.unlock?.().catch(() => null)
  if (!result || result.missing) return { problem: 'A digital não está ativa neste celular. Entre com a senha.' }
  if (result.invalidated) {
    if (result.id) write(s => s.setItem(PENDING_REVOKE_KEY, result.id))
    forgetCredential()
    return { problem: 'Uma digital nova foi cadastrada neste celular. Por segurança, entre com a senha e ative a digital de novo.' }
  }
  if (result.cancelled) return { cancelled: true, usePassword: !!result.usePassword }
  if (result.error) return { problem: result.error }

  const signed = await signInWithBiometric(result.token, await nativeDeviceModel())
  if ('problem' in signed) {
    if (signed.refused) {
      await plugin()?.disable?.().catch(() => null) // venceu, senha mudou, desligada: o token não volta a valer
      forgetCredential()
    }
    return { problem: signed.problem }
  }
  return { session: signed }
}

// ── Trava do app ao voltar do segundo plano ──────────────────────────────────────────────────────────

/** O painel mostra a tela de bloqueio (AppLockOverlay.vue) enquanto isto for verdadeiro. */
export const appLocked = ref(false)
let lockMinutes = 0
let pausedAt: number | null = null

/** Configurações Gerais › App de celular › Travar o app depois de (min) fora. 0 = não trava. */
export function setAppLockMinutes(minutes: number) {
  lockMinutes = Math.max(0, minutes)
}

/**
 * Liga a trava (só no app): voltando do segundo plano depois do tempo configurado, com a digital ativa neste celular e
 * alguém logado, mostra a tela de bloqueio e pede a digital (ou a senha do aparelho). Devolve como parar.
 */
export function startAppLock(isLoggedIn: () => boolean): () => void {
  if (!isNativeApp()) return () => undefined
  const stopPause = onAppPause(() => { pausedAt = Date.now() })
  const stopResume = onAppResume(() => {
    const away = pausedAt === null ? 0 : Date.now() - pausedAt
    pausedAt = null
    if (appLocked.value || lockMinutes <= 0 || !isLoggedIn() || away < lockMinutes * 60_000) return
    void biometricStatus().then(status => {
      if (!status?.hasCredential) return
      appLocked.value = true
      void unlockApp()
    })
  })
  return () => { stopPause(); stopResume() }
}

/** Botão Desbloquear da tela de bloqueio. */
export async function unlockApp(): Promise<boolean> {
  const result = await plugin()?.authenticate?.().catch(() => null)
  if (!result?.ok) return false
  appLocked.value = false
  return true
}

/** Só para testes. */
export function resetBiometricForTests() {
  appLocked.value = false
  lockMinutes = 0
  pausedAt = null
}
